import React, { useEffect, useMemo, useState } from "react";

import { padCsvRows, parseCsv } from "../helpers/csvParse";

export interface CsvViewerProps {
  /** CSV text, or a `blob:` / `http(s):` URL to fetch as UTF-8 text */
  source: string;
  fileName?: string;
  /** First row rendered as `<th>` (default true). */
  headerRow?: boolean;
}

const MAX_PREVIEW_ROWS = 5000;

function isFetchableUrl(s: string): boolean {
  return (
    s.startsWith("blob:") ||
    s.startsWith("http://") ||
    s.startsWith("https://")
  );
}

const CsvViewer: React.FC<CsvViewerProps> = ({
  source,
  fileName,
  headerRow = true,
}) => {
  const [text, setText] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);

    if (!source) {
      setText("");
      return;
    }

    if (!isFetchableUrl(source)) {
      setText(source);
      return;
    }

    (async () => {
      try {
        const isHttp =
          source.startsWith("http://") || source.startsWith("https://");
        const res = await fetch(source, {
          ...(isHttp
            ? {
                credentials: "include" as RequestCredentials,
                mode: "cors" as RequestMode,
              }
            : {}),
        });
        if (!res.ok) {
          throw new Error(`Failed to load file (${res.status})`);
        }
        const body = await res.text();
        if (!cancelled) setText(body);
      } catch (e) {
        if (!cancelled) {
          setText("");
          setError(
            e instanceof Error ? e.message : "Could not read CSV file.",
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [source]);

  const { rows, truncated } = useMemo(() => {
    const raw = parseCsv(text);
    const padded = padCsvRows(raw);
    if (padded.length <= MAX_PREVIEW_ROWS) {
      return { rows: padded, truncated: false };
    }
    return {
      rows: padded.slice(0, MAX_PREVIEW_ROWS),
      truncated: true,
    };
  }, [text]);

  const thead = headerRow && rows.length > 0 ? rows[0]! : null;
  const bodyRows = headerRow && rows.length > 0 ? rows.slice(1) : rows;

  if (error) {
    return (
      <div className="flex-1 min-h-0 overflow-auto p-6 text-center text-md text-hex-gray-600 border-t border-hex-gray-100">
        {error}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex-1 min-h-0 overflow-auto p-6 text-center text-md text-hex-gray-500 bg-white border-t border-hex-gray-100">
        Empty or invalid CSV.
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-white border-t border-hex-gray-100">
      {truncated && (
        <p className="text-sm text-hex-gray-600 px-4 py-2 border-b border-hex-gray-200 shrink-0">
          Showing first {MAX_PREVIEW_ROWS.toLocaleString()} rows.
        </p>
      )}
      <div className="flex-1 min-h-0 overflow-auto">
        <table
          className="border-collapse text-left text-sm w-full min-w-max"
          style={{
            fontFamily:
              'Calibri, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
          }}
          aria-label={fileName ? `CSV preview: ${fileName}` : "CSV preview"}
        >
          {thead && (
            <thead className="sticky top-0 z-10 bg-hex-gray-100 shadow-xs">
              <tr>
                {thead.map((cell, j) => (
                  <th
                    key={j}
                    scope="col"
                    className="border border-hex-gray-300 px-3 py-2 font-semibold text-hex-gray-900 whitespace-nowrap"
                  >
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {bodyRows.map((r, i) => (
              <tr
                key={i}
                className={i % 2 === 0 ? "bg-white" : "bg-hex-gray-50/80"}
              >
                {r.map((cell, j) => (
                  <td
                    key={j}
                    className="border border-hex-gray-200 px-3 py-1.5 text-hex-gray-800 max-w-md align-top wrap-break-word whitespace-pre-wrap"
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CsvViewer;
